'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { FileAttachField } from '~/components/ui/file-attach-field'
import { Label } from '~/components/ui/label'
import { Textarea } from '~/components/ui/textarea'
import { createClient } from '~/lib/supabase/client'
import { withImpersonation } from '~/lib/impersonation-client'
import { presignBugReportUpload, uploadToS3 } from '~/lib/uploads'
import { collectTicketContext } from '~/lib/ticket-context'
import { env } from '~/env'

// S-2 — bug-report intake. Shared dialog used by:
//   - The floating help button (HelpButton) on every page.
//   - The "Report a problem" card on /settings (entry 6).
// Screenshot upload goes through the existing X-2 IStorageProvider via
// `/uploads/presign-bug-report`. The object is PRIVATE and lives under
// `bug_report_uploads/<userId>/<uuid>` (never made publicly readable); the resulting key,
// original filename, type and size are stored in `support_messages.attachments_json`
// by the API. There is no public URL — reading a screenshot back needs an
// authorised, signed download.

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

interface BugReportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Defaults to BUG; settings-page wrapper can override to QUESTION/FEEDBACK. */
  kind?: 'BUG' | 'QUESTION' | 'FEEDBACK' | 'OTHER'
  /** Pre-fill the description with route + user-agent context. */
  defaultContext?: string
  /** Called after a ticket is successfully created (e.g. to refresh a list). */
  onSubmitted?: (ticketId: string) => void
}

const MAX_SCREENSHOT_MB = 8

export function BugReportDialog({
  open,
  onOpenChange,
  kind = 'BUG',
  defaultContext,
  onSubmitted,
}: BugReportDialogProps) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState(defaultContext ?? '')
  const [severity, setSeverity] = useState<Severity>('MEDIUM')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (open) {
      setSubject('')
      setBody(defaultContext ?? '')
      setSeverity('MEDIUM')
      setFile(null)
      setError(null)
      setSubmittedTicketId(null)
    }
  }, [open, defaultContext])

  const submit = useCallback(async () => {
    const trimmedSubject = subject.trim()
    const trimmedBody = body.trim()
    if (!trimmedSubject) {
      setError('A short subject is required.')
      return
    }
    if (!trimmedBody) {
      setError('Tell us what happened — even a sentence helps.')
      return
    }
    if (file && file.size > MAX_SCREENSHOT_MB * 1024 * 1024) {
      setError(`Screenshot must be under ${MAX_SCREENSHOT_MB} MB.`)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const supabase = createClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) {
        setError('Please sign in to report a problem.')
        setSubmitting(false)
        return
      }

      let attachments:
        | Array<{ key: string; filename: string; content_type: string; size: number }>
        | undefined
      if (file) {
        // Private: the key is an opaque `bug_report_uploads/<userId>/<uuid>`, so the
        // original filename is kept on the attachment record instead.
        const { url, key, headers } = await presignBugReportUpload(
          file.name,
          file.type || 'application/octet-stream',
          token,
        )
        await uploadToS3(url, file, headers)
        attachments = [{ key, filename: file.name, content_type: file.type, size: file.size }]
      }

      // Automatic diagnostic context (page URL, browser, recent errors,
      // correlation ids). Best-effort — a collection failure never blocks
      // the report.
      const context = await collectTicketContext().catch(() => undefined)

      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/support/tickets`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${withImpersonation(token)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: trimmedSubject,
          body: trimmedBody,
          kind,
          severity,
          attachments,
          context,
        }),
      })
      if (res.status === 429) {
        setError(
          'Daily ticket limit reached. Try again in a few hours or email support@pathcollective.com.',
        )
        setSubmitting(false)
        return
      }
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { message?: string } | null
        setError(data?.message ?? `Failed to submit (${res.status}).`)
        setSubmitting(false)
        return
      }
      const created = (await res.json()) as { id: string }
      setSubmittedTicketId(created.id)
      onSubmitted?.(created.id)
    } catch (err) {
      setError(`Failed to submit: ${(err as Error).message}`)
    } finally {
      setSubmitting(false)
    }
  }, [subject, body, severity, file, kind, onSubmitted])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="bug-report-dialog"
      >
        <DialogHeader>
          <DialogTitle>Report a problem</DialogTitle>
          <DialogDescription>
            Tell us what happened. A short description and a screenshot go a long way.
          </DialogDescription>
        </DialogHeader>

        {submittedTicketId ? (
          <div className="space-y-3" data-testid="bug-report-success">
            <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              Thanks — we&rsquo;ve got it. Your ticket reference is
              {' '}
              <code className="rounded bg-white px-1 py-0.5">{submittedTicketId}</code>.
              We&rsquo;ll reply by email.
            </p>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="bug-subject">What went wrong?</Label>
              <Input
                id="bug-subject"
                data-testid="bug-report-subject"
                placeholder="One-line summary"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={140}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bug-body">Details</Label>
              <Textarea
                id="bug-body"
                data-testid="bug-report-body"
                placeholder="Steps to reproduce, what you expected, what happened…"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="bug-severity">Severity</Label>
                <Select value={severity} onValueChange={(v) => setSeverity(v as Severity)}>
                  <SelectTrigger id="bug-severity" data-testid="bug-report-severity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent — blocking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="bug-screenshot">Screenshot (optional)</Label>
                <FileAttachField
                  id="bug-screenshot"
                  data-testid="bug-report-screenshot"
                  ref={fileInputRef}
                  accept="image/*"
                  buttonText="Attach screenshot"
                  onFilesSelected={(files) => setFile(files[0] ?? null)}
                />
                {file ? (
                  <div className="flex items-center justify-between text-xs text-neutral">
                    <span className="truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null)
                        if (fileInputRef.current) fileInputRef.current.value = ''
                      }}
                      className="ml-2 inline-flex items-center text-primary hover:underline"
                    >
                      <X className="mr-1 h-3 w-3" /> Remove
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-neutral">
                    Max {MAX_SCREENSHOT_MB} MB. PNG, JPG, GIF.
                  </p>
                )}
              </div>
            </div>

            <p className="text-xs text-neutral">
              Diagnostic info (page URL, browser, recent errors) is included
              automatically to help us fix the problem faster.
            </p>

            {error ? (
              <p
                className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800"
                data-testid="bug-report-error"
              >
                {error}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void submit()}
                disabled={submitting}
                data-testid="bug-report-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
